import { Injectable } from '@angular/core';
import {
  CTMeasurementFiltrableItemDto,
  CTMeasurementFiltrableType,
} from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/ct-measurement-filtrable-item';
import {
  CTResultFiltrableItemDto,
  CTResultFiltrableType,
} from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/ct-result-filtrable-item';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import { MeasurementDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { IFiltrableItemDto } from '@common-modules/shared/model/filtrable-items/filtrable-item.dto';
import { FiltrableItemTypeEnum } from '@common-modules/shared/model/filtrable-items/types/filtrable-item-type-enum';
import { Observable, forkJoin, map, of } from 'rxjs';
import { DashboardWidgetTypes } from '../models/dashboard-widget-types';
import { DashboardCartItem } from './dashboard-cart-item';

@Injectable()
export class DashboardCartFiltrableItemService {
  buildDashboardCartAndSettings(
    cartItems: DashboardCartItem[],
    widgetType: DashboardWidgetTypes,
    timeStep: Date,
    dashboardChildId?: string
  ): Observable<DashboardCartAndSettings> {
    const items$: Observable<IFiltrableItemDto>[] = cartItems.map((cartItem) => {
      switch (cartItem.filtrableType) {
        case FiltrableItemTypeEnum.CTResult:
          return this.mapResultCartToFiltrable(cartItem);
        case FiltrableItemTypeEnum.CTMeasurement:
          return this.mapMeasurementCartToFiltrable(cartItem);
      }
    });

    const visibleCartItems = cartItems.filter((item) => item.isVisible !== false);
    let visibleItems$: Observable<IFiltrableItemDto[]>;
    if (visibleCartItems.length === 0) {
      visibleItems$ = of([]);
    } else {
      const arr: Observable<IFiltrableItemDto>[] = visibleCartItems.map((cartItem) => {
        switch (cartItem.filtrableType) {
          case FiltrableItemTypeEnum.CTResult:
            return this.mapResultCartToFiltrable(cartItem);
          case FiltrableItemTypeEnum.CTMeasurement:
            return this.mapMeasurementCartToFiltrable(cartItem);
        }
      });
      visibleItems$ = forkJoin(arr);
    }

    return forkJoin([forkJoin(items$), visibleItems$]).pipe(
      map(([items, visibleItems]) => {
        const cartSelection = new DashboardCartAndSettings({
          dashboardChildId: dashboardChildId ?? globalUtilsHelper.generateGuid(),
          items,
          visibleItems: globalUtilsHelper.clone(visibleItems, true),
          timeStep,
          widgetType,
        });
        return cartSelection;
      })
    );
  }

  buildCartItems(settings: DashboardCartAndSettings): Observable<DashboardCartItem[]> {
    const visibleKeys = (settings.visibleItems || []).map((item) => item.filtrableType?.key);
    const cartItems$ = (settings.items || []).map((item) => {
      switch (item.filtrableType.type) {
        case FiltrableItemTypeEnum.CTResult:
          const key = item.filtrableType?.key;
          const isVisible = visibleKeys.includes(key);
          return this.mapResultFiltrableToCart(item as CTResultFiltrableItemDto, isVisible);
        case FiltrableItemTypeEnum.CTMeasurement:
          return this.mapMeasurementFiltrableToCart(item as CTMeasurementFiltrableItemDto);
      }
    });
    return forkJoin(cartItems$);
  }

  getAttributeCartItemId = (attributeId: string, networkElementId: string): string =>
    `${attributeId}.${networkElementId}`;

  private mapResultFiltrableToCart(
    filtrableItem: CTResultFiltrableItemDto,
    isVisible: boolean = true
  ): Observable<DashboardCartItem> {
    const filtrableType = filtrableItem.filtrableType;
    const id = this.getAttributeCartItemId(
      filtrableType.attributeId,
      filtrableType.networkElementId
    );
    const cartItem: DashboardCartItem = {
      id,
      filtrableType: FiltrableItemTypeEnum.CTResult,
      params: {
        settingsItem: filtrableType.workspaceConnectSetting,
        element: {
          networkElementTypeId: filtrableType.networkElementTypeId,
          networkElementId: filtrableType.networkElementId,
          networkElementName: filtrableType.networkElementName,
          attributes: [], // Added to satisfy NetworkElementResult type
        },
        attributeId: filtrableType.attributeId,
      },
      isVisible,
      title$: of(filtrableType.title),
      subtitle$: of(filtrableType.subtitle),
    };
    return of(cartItem);
  }

  private mapMeasurementFiltrableToCart(
    filtrableItem: CTMeasurementFiltrableItemDto
  ): Observable<DashboardCartItem> {
    const filtrableType = filtrableItem.filtrableType as CTMeasurementFiltrableType;
    const measurement = new MeasurementDto({ pointId: filtrableType.pointId });
    const cartItem: DashboardCartItem = {
      id: filtrableType.key,
      filtrableType: FiltrableItemTypeEnum.CTMeasurement,
      params: {
        measurement: measurement,
        settingsItem: filtrableType.workspaceConnectSetting,
      },
      isVisible: true,
      title$: of(filtrableType.title),
      subtitle$: of(filtrableType.subtitle ?? ''),
    };
    return of(cartItem);
  }

  private mapResultCartToFiltrable(cartItem: DashboardCartItem): Observable<IFiltrableItemDto> {
    const settingsItem = cartItem?.params?.settingsItem;
    const element = cartItem?.params?.element;

    return forkJoin([cartItem.title$, cartItem.subtitle$ ?? of(null)]).pipe(
      map(([title, subtitle]) => {
        const filtrableType = new CTResultFiltrableType({
          title,
          subtitle,
          attributeId: cartItem.params.attributeId,
          workspaceConnectSetting: settingsItem,
          networkElementTypeId: element.networkElementTypeId,
          networkElementId: element.networkElementId,
          networkElementName: element.networkElementName,
          units: cartItem?.params?.units,
          physType: cartItem?.params?.physType,
        });

        const filtrableItem: CTResultFiltrableItemDto = {
          startDate: undefined,
          endDate: undefined,
          filtrableType,
        };

        return filtrableItem;
      })
    );
  }

  private mapMeasurementCartToFiltrable(
    cartItem: DashboardCartItem
  ): Observable<CTMeasurementFiltrableItemDto> {
    const settingsItem = cartItem?.params?.settingsItem;
    const measurement = cartItem?.params?.measurement;

    return forkJoin([cartItem.title$, cartItem.subtitle$ ?? of(null)]).pipe(
      map(([title, subtitle]) => {
        const filtrableType = new CTMeasurementFiltrableType({
          pointId: measurement.pointId,
          title,
          subtitle,
          workspaceConnectSetting: settingsItem,
          unitType: measurement.unitType,
        });

        const filtrableItem: CTMeasurementFiltrableItemDto = {
          startDate: undefined,
          endDate: undefined,
          filtrableType,
        };

        return filtrableItem;
      })
    );
  }
}
